import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import { Button, Col, Input, Modal, Row, Spin, Table, message } from "antd";
import { FormInstance } from "antd/lib/form/Form";
import { AxiosResponse } from "axios";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import {
  CREATE_TEMPLATE_CATEGORY,
  GET_TEMPLATE_CATEGORIES,
  GET_TEMPLATE_CATEGORY,
  UPDATE_TEMPLATE_CATEGORY,
} from "../gql/templates";
import {
  APIError,
  FormFieldConfig,
  TemplateCategory,
} from "../helpers/shared-interfaces";
import { handleValidationErrors, request } from "../helpers/utilities";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  isLoading: boolean;
  isSaving: boolean;
  page: number;
  pageSize: number;
  searchTerm: string;
  templateCategories: TemplateCategory[];
  templateCategoriesCount: number;
  showModal: boolean;
  selectedTemplateCategory?: TemplateCategory;
}

export const templateCategoryColumns = (
  onEdit: (templateCategoryId: string) => void,
): Array<Record<string, unknown>> => [
  {
    title: "Category Name",
    dataIndex: "name",
    key: "name",
    render(text: string): JSX.Element {
      return (
        <div style={{ fontWeight: 600 }}>{text}</div>
      );
    },
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    render(text: string): JSX.Element {
      return (
        <div style={{ color: "rgba(0, 0, 0, 0.45)", fontSize: "14px" }}>
          {text || "Used to organize communication templates during creation and review."}
        </div>
      );
    },
  },
  {
    title: "Action",
    dataIndex: "action",
    key: "action",
    align: "center" as const,
    render(_: string, record: TemplateCategory): JSX.Element {
      return (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => onEdit(record.id)}
        >
          Edit
        </Button>
      );
    },
  },
];

export default class TemplateCategories extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;

  constructor(props: RouteComponentProps) {
    super(props);
    this.reference = React.createRef<FormInstance>();
    this.state = {
      isLoading: false,
      isSaving: false,
      page: 1,
      pageSize: 10,
      searchTerm: "",
      templateCategories: [],
      templateCategoriesCount: 0,
      showModal: false,
    };
  }

  componentDidMount(): void {
    this.setState({ isLoading: true }, this.getTemplateCategories);
  }

  getTemplateCategories = async (): Promise<void> => {
    const { page, pageSize, searchTerm } = this.state;

    try {
      const {
        data: { data },
      }: AxiosResponse<{
        data: {
          templateCategories: {
            count: number;
            results: TemplateCategory[];
          };
        };
      }> = await request({
        query: GET_TEMPLATE_CATEGORIES,
        variables: {
          page,
          q: searchTerm || undefined,
          numberPerPage: pageSize,
        },
      });

      this.setState({
        templateCategories: data.templateCategories.results,
        templateCategoriesCount: data.templateCategories.count,
        isLoading: false,
      });
    } catch (error) {
      this.setState({ isLoading: false });
      message.error(`Failed to load template categories - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  handleSearch = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    e.persist();
    this.setState(
      {
        searchTerm: e.target.value,
        page: 1,
        isLoading: true,
      },
      this.getTemplateCategories,
    );
  };

  nextPage = async (page: number, pageSize?: number): Promise<void> => {
    this.setState(
      {
        page,
        pageSize: pageSize || this.state.pageSize,
        isLoading: true,
      },
      this.getTemplateCategories,
    );
  };

  toggleModal = async (templateCategoryId?: string): Promise<void> => {
    if (!this.state.showModal && templateCategoryId) {
      this.setState({ isLoading: true });
      try {
        const {
          data: { data },
        }: AxiosResponse<{
          data: {
            templateCategory: TemplateCategory;
          };
        }> = await request({
          query: GET_TEMPLATE_CATEGORY,
          variables: { id: templateCategoryId },
        });

        this.setState({
          selectedTemplateCategory: data.templateCategory,
          showModal: true,
          isLoading: false,
        });
      } catch (error) {
        this.setState({ isLoading: false });
        message.error(`Failed to load template category - ${generateDisplayErrorMessage(error)}`);
      }

      return;
    }

    this.setState(
      (prevState) => ({
        showModal: !prevState.showModal,
      }),
      () => {
        if (!this.state.showModal) {
          this.reference.current?.resetFields(["name"]);
          this.setState({ selectedTemplateCategory: undefined });
        }
      },
    );
  };

  saveTemplateCategory = async (): Promise<void> => {
    interface Response {
      data?: {
        createTemplateCategory?: {
          templateCategory?: TemplateCategory;
          errors?: APIError[];
        };
        updateTemplateCategory?: {
          templateCategory?: TemplateCategory;
          errors?: APIError[];
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(["name", "description"]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    const { selectedTemplateCategory } = this.state;

    this.setState({ isSaving: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: selectedTemplateCategory
          ? UPDATE_TEMPLATE_CATEGORY
          : CREATE_TEMPLATE_CATEGORY,
        variables: selectedTemplateCategory
          ? { id: selectedTemplateCategory.id, name: data.name, description: data.description }
          : { name: data.name, description: data.description },
      });

      const payload = selectedTemplateCategory
        ? response.data.data?.updateTemplateCategory
        : response.data.data?.createTemplateCategory;

      if (payload?.errors && payload.errors.length > 0) {
        message.error(payload.errors[0].description);
      } else {
        message.success(
          selectedTemplateCategory
            ? "Template category updated successfully"
            : "Template category created successfully",
        );
        await this.toggleModal();
        this.setState({ isLoading: true }, this.getTemplateCategories);
      }
    } catch (error) {
      message.error(`Failed to save template category - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isSaving: false });
    }
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      isSaving,
      page,
      pageSize,
      searchTerm,
      showModal,
      selectedTemplateCategory,
      templateCategories,
      templateCategoriesCount,
    } = this.state;

    const fields: FormFieldConfig[] = [
      {
        name: "name",
        label: "Category Name",
        inputType: "string",
        placeholder: "Enter template category name",
        rule: "string",
        initialValue: selectedTemplateCategory?.name,
        required: true,
        colConfig: {
          span: 24,
        },
      },
      {
        name: "description",
        label: "Category Description",
        inputType: "textArea",
        placeholder: "Enter template category description",
        rule: "string",
        initialValue: selectedTemplateCategory?.description,
        required: false,
        colConfig: {
          span: 24,
        },
      },
    ];

    return (
      <Spin spinning={isLoading}>
        <Card>
          <Modal
            title={
              selectedTemplateCategory
                ? "Edit Template Category"
                : "Create Template Category"
            }
            visible={showModal}
            onCancel={() => this.toggleModal()}
            onOk={this.saveTemplateCategory}
            okText={
              selectedTemplateCategory ? "Save Changes" : "Create Category"
            }
            okButtonProps={{ className: "base-button", loading: isSaving }}
            destroyOnClose
          >
            <Spin spinning={isSaving}>
              <BaseForm
                key={selectedTemplateCategory?.id || "new-template-category"}
                layout="vertical"
                reference={this.reference}
                fieldsToHide={[]}
                fields={fields}
              />
            </Spin>
          </Modal>

          <Row style={{ marginBottom: "20px" }} gutter={16}>
            <Col span={12}>
              <div style={{ color: "rgba(0, 0, 0, 0.65)", marginTop: "8px" }}>
                Manage the categories your team uses when creating communication
                templates.
              </div>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <EntitledSection entitlement={entitlements.Templates.create}>
                <Button
                  className="base-button"
                  size="large"
                  onClick={() => this.toggleModal()}
                >
                  <PlusOutlined />
                  Add Template Category
                </Button>
              </EntitledSection>
            </Col>
          </Row>

          <Row style={{ marginBottom: "16px" }} gutter={16}>
            <Col span={10}>
              <Input.Search
                size="large"
                placeholder="Search template categories"
                value={searchTerm}
                onChange={this.handleSearch}
                allowClear
              />
            </Col>
            <Col
              span={14}
              style={{ textAlign: "right", color: "rgba(0, 0, 0, 0.65)" }}
            >
              {templateCategoriesCount} categories available
            </Col>
          </Row>

          <Table
            bordered={true}
            columns={templateCategoryColumns((templateCategoryId) =>
              this.toggleModal(templateCategoryId),
            )}
            dataSource={templateCategories.map((templateCategory) => ({
              ...templateCategory,
              key: templateCategory.id,
            }))}
            pagination={{
              current: page,
              pageSize,
              total: templateCategoriesCount,
              onChange: this.nextPage,
            }}
          />
        </Card>
      </Spin>
    );
  }
}
